import { createSignal, createEffect, Show, createMemo, onMount, onCleanup } from "solid-js";
import { hexColor, Text, View } from "@lightningtv/solid";
import { playListPassword, playListUrl, playListUsername, setGlobalStream } from "../../../state";
import axios from "axios";
import { Buffer } from "buffer";
import { dnsResolver } from "../../api/providers/dnsResolver";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import { decodeBase64UTF8 } from "../../utils/encoding";
import { getProxyHeaders, getProxyUrl } from "../../api/proxyHeaders";

const OSD = (props: any) => {
  const [epgData, setEpgData] = createSignal<any>(null);
  const [screenWidth] = createSignal(window.innerWidth);
  const [secondProgramTime, setSecondProgramTime] = createSignal("");
  const [thirdProgramTime, setThirdProgramTime] = createSignal("");
  const [listOfFav, setListOfFav] = createSignal<any>({});
  const [isFavChannel, setIsFavChannel] = createSignal(false);
  const [currentTime, setCurrentTime] = createSignal("");
  const [currentDate, setCurrentDate] = createSignal("");
  const [progressPercent, setProgressPercent] = createSignal(0);
  const [startTimeStr, setStartTimeStr] = createSignal("");
  const [endTimeStr, setEndTimeStr] = createSignal("");

  // ── Clock ─────────────────────────────────────────────────────────────
  const updateClock = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    setCurrentTime(`${hh}:${mm}`);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    setCurrentDate(`${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]}`);
  };

  onMount(() => {
    updateClock();
    const interval = setInterval(updateClock, 30000);
    onCleanup(() => clearInterval(interval));
  });

  function formatTimeRange(start: string, end: string) {
    const format = (str: string) => {
      const date = new Date(str.replace(" ", "T"));
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${hours}:${minutes}`;
    };
    return `${format(start)} - ${format(end)}`;
  }

  function formatTimeSingle(str: string) {
    const date = new Date(str.replace(" ", "T"));
    const hh = String(date.getHours()).padStart(2, "0");
    const mm = String(date.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }

  // ── EPG fetch (logic unchanged) ───────────────────────────────────────
  createEffect(async () => {
    const username = playListUsername();
    const password = playListPassword();
    const playList = playListUrl();
    const vpnStatus = localStorage.getItem("vpn");
    const proxyUrl = localStorage.getItem("proxy_url");

    setGlobalStream(props.channel.stream_id);

    let originalApiUrl = `${playList}/player_api.php?username=${username}&password=${password}&action=get_short_epg&stream_id=${props.channel.stream_id}`;
    let finalApiUrl = originalApiUrl;
    let headers = {};

    try {
      const dnsResult = await dnsResolver.transformUrl(originalApiUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalApiUrl = dnsResult.url;
        headers['Host'] = dnsResult.originalHost;
      } else if (typeof dnsResult === 'string' && dnsResult !== originalApiUrl) {
        finalApiUrl = dnsResult;
      }
    } catch (dnsError) {
      finalApiUrl = originalApiUrl;
    }

    if (vpnStatus === "on" && proxyUrl) {
      const bufferedUrl = Buffer.from(finalApiUrl, "utf-8").toString("base64");
      finalApiUrl = `${proxyUrl}${bufferedUrl}`;
      Object.assign(headers, getProxyHeaders());
    }

    axios
      .get(finalApiUrl, { headers })
      .then(response => {
        setEpgData(response.data);
      })
      .catch(error => {
        setEpgData(null);
      });
  }, [props]);

  // ── Memos (logic unchanged) ───────────────────────────────────────────
  const firstProgram = createMemo(() => {
    const data = epgData()?.epg_listings;
    return data?.[0] ? decodeBase64UTF8(data[0].title) : `Programs of ${props.channel.name}`;
  });

  const secondProgram = createMemo(() => {
    const data = epgData()?.epg_listings;
    if (data?.[1]) {
      setSecondProgramTime(formatTimeSingle(data[1].start));
      return decodeBase64UTF8(data[1].title);
    }
    return `Programs of ${props.channel.name}`;
  });

  const thirdProgram = createMemo(() => {
    const data = epgData()?.epg_listings;
    if (data?.[2]) {
      setThirdProgramTime(formatTimeSingle(data[2].start));
      return decodeBase64UTF8(data[2].title);
    }
    return `Programs of ${props.channel.name}`;
  });

  const epgFont = (text: string) =>
    /[؀-ۿ]/.test(text) ? "NotoSansArabic" : fontFamily();

  function calculateProgress(startTimeUnix: any, endTimeUnix: any) {
    const currentTime = Date.now() / 1000;
    const total = endTimeUnix - startTimeUnix;
    const elapsed = currentTime - startTimeUnix;
    return Math.min(1, Math.max(0, elapsed / total));
  }

  const truncate = (str: string, maxLength = 30) =>
    str?.length > maxLength ? str.slice(0, maxLength) + "..." : str;

  const addToFavorites = () => {
    const currentPlaylistUrl = playListUrl();
    const existingChannels = localStorage.getItem("@favoriteChannels");
    let existingChannelsArray = existingChannels ? JSON.parse(existingChannels) : [];
    const isChannelExist = existingChannelsArray.some(
      (c) => c.stream_id === props.channel.stream_id && c.playlistUrl === currentPlaylistUrl
    );
    if (!isChannelExist) {
      existingChannelsArray.push({ ...props.channel, playlistUrl: currentPlaylistUrl });
    } else {
      existingChannelsArray = existingChannelsArray.filter(
        (c) => !(c.stream_id === props.channel.stream_id && c.playlistUrl === currentPlaylistUrl)
      );
    }
    localStorage.setItem("@favoriteChannels", JSON.stringify(existingChannelsArray));
    const currentPlaylistFavorites = existingChannelsArray.filter(c => c.playlistUrl === currentPlaylistUrl);
    setListOfFav(currentPlaylistFavorites);
    setIsFavChannel(currentPlaylistFavorites.some(c => c.stream_id === props.channel.stream_id));
  };

  createEffect(() => {
    if (props.channel?.stream_id) {
      const currentPlaylistUrl = playListUrl();
      const existingChannels = localStorage.getItem("@favoriteChannels");
      const existingChannelsArray = existingChannels ? JSON.parse(existingChannels) : [];
      setIsFavChannel(
        existingChannelsArray.some(
          (c) => c.stream_id === props.channel.stream_id && c.playlistUrl === currentPlaylistUrl
        )
      );
    }
  });

  const encodedUrl = props.channel.stream_icon ? btoa(props.channel.stream_icon) : "";

  // Progress bar total width
  const BAR_W = 1100;

  return (
    <View>
      {/* ── OSD bottom bar ─────────────────────────────────────────────── */}
      <View
        x={0}
        y={820}
        width={1920}
        height={260}
        color={hexColor("#00000000")}
      >
        {/* Smooth gradient scrim: fully transparent at the top, fading into a
            near-opaque dark at the screen bottom so the OSD blends over the
            video instead of showing a hard bar. Extends above the bar (negative
            y) to lengthen the fade. */}
        <View
          x={0}
          y={-140}
          width={1920}
          height={400}
          colorTop={hexColor("#0b0d1200")}
          colorBottom={hexColor("#0b0d12f2")}
        />

        {/* ── LEFT: Channel logo + number ── */}
        <View x={60} y={70}>
          {/* Logo box */}
          <View
            width={100} height={100}
            borderRadius={12}
            color={hexColor("#12151c")}
          >
            <View
              x={10} y={10}
              width={80} height={80}
              borderRadius={8}
              src={
                props.channel.stream_icon
                  ? `${getProxyUrl()}${encodedUrl}`
                  : "./assets/television.png"
              }
            />
          </View>

          {/* Channel number below logo */}
          <Text x={25} y={115} style={{
            fontSize: 22,
            color: hexColor("#9ca3af"),
            fontFamily: fontFamily(),
          }}>
            {props.channel.num}
          </Text>
        </View>

        {/* ── CENTER: Channel info ── */}
        <View x={200} y={40}>
          {/* Channel name */}
          <Text style={{
            fontSize: 32,
            color: hexColor("#f0f6fc"),
            fontWeight: isRTL() ? "normal" : "bold",
            fontFamily: fontFamily(),
          }}>
            {props.channel.name}
          </Text>

          {/* Now badge + program title */}
          <View y={44} style={{ flexDirection: "row" }} gap={12}>
            {/* "Now:" pill */}
            <View
              width={70} height={30}
              borderRadius={6}
              color={hexColor("#2563eb")}
            >
              <Text x={14} y={6} style={{
                fontSize: 18,
                color: hexColor("#ffffff"),
                fontWeight: isRTL() ? "normal" : "bold",
                fontFamily: fontFamily(),
              }}>
                Now:
              </Text>
            </View>

            <Show when={epgData()?.epg_listings?.[0]}>
              <Text x={80} y={4} style={{
                fontSize: 22,
                color: hexColor("#ffffff"),
                fontFamily: epgFont(firstProgram()),
                fontWeight: /[؀-ۿ]/.test(firstProgram()) ? 400 : 700,
              }}>
                {truncate(firstProgram(), 34)}
              </Text>
            </Show>
          </View>

          {/* Times row */}
          <Show when={epgData()?.epg_listings?.[0]}>
            {() => {
              const listing = epgData().epg_listings[0];
              const startTime = listing.start_timestamp;
              const endTime = listing.stop_timestamp;
              const progress = calculateProgress(startTime, endTime);
              const pct = Math.round(progress * 100);
              const filledW = Math.round(progress * BAR_W);

              const fmtTs = (ts: number) => {
                const d = new Date(ts * 1000);
                return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
              };

              return (
                <View y={110}>
                  {/* Start time */}
                  <Text x={0} y={-20} style={{
                    fontSize: 20,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {fmtTs(startTime)}
                  </Text>

                  {/* Progress bar */}
                  <View x={0} y={4} width={BAR_W} height={5} borderRadius={4} color={hexColor("#1e2a3a")}>
                    <View width={filledW} height={5} borderRadius={4} color={hexColor("#2563eb")} />
                  </View>

                  {/* Percentage — centered above bar */}
                  <Text x={550} y={-18} style={{
                    fontSize: 20,
                    color: hexColor("#60a5fa"),
                    fontWeight: isRTL() ? "normal" : "bold",
                    fontFamily: fontFamily(),
                  }}>
                    {pct}%
                  </Text>

                  {/* End time */}
                  <Text x={1050} y={-20} style={{
                    fontSize: 20,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {fmtTs(endTime)}
                  </Text>
                </View>
              );
            }}
          </Show>

          {/* Favorite button */}
          <View
            y={142}
            width={isFavChannel() ? 300 : 260}
            height={52}
            borderRadius={12}
            color={hexColor("#12151c")}
            style={{
              border: { width: 1, color: hexColor("#1e2a3a") },
              focus: { color: hexColor("#1e3a5f"), border: { width: 1, color: hexColor("#2563eb") } },
            }}
            onEnter={() => addToFavorites()}
            autofocus={props.isLocked ? false : true}
          >
            {/* Heart icon */}
            <View
              x={18} y={14}
              width={24} height={24}
              src={
                isFavChannel()
                  ? "https://i.ibb.co/pRyqmgX/icons8-love-100-2.png"
                  : "https://i.ibb.co/kHcHCDb/icons8-love-100.png"
              }
            />
            <Text x={52} y={14} style={{
              fontSize: 22,
              color: hexColor("#d1d5db"),
              fontFamily: fontFamily(),
            }}>
              {isFavChannel() ? t("removeFromFav") : t("addToFav")}
            </Text>
          </View>
        </View>

        {/* ── RIGHT: Next programs ── */}
        <Show when={secondProgramTime() || thirdProgramTime()}>
          <View x={1400} y={60}>
            {/* "NEXT:" label */}
            <Text style={{
              fontSize: 20,
              color: hexColor("#6b7280"),
              fontWeight: isRTL() ? "normal" : "bold",
              fontFamily: fontFamily(),
              letterSpacing: 1,
            }}>
              NEXT:
            </Text>

            {/* Program 2 — only if time exists */}
            <Show when={secondProgram() && secondProgramTime()}>
              <View y={36} style={{ flexDirection: "row" }} gap={14}>
                <View width={70} height={34} borderRadius={8} color={hexColor("#12151c")}>
                  <Text x={8} y={8} style={{
                    fontSize: 18,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {secondProgramTime()}
                  </Text>
                </View>
                <Text x={80} y={6} style={{
                  fontSize: 20,
                  color: hexColor("#d1d5db"),
                  fontFamily: epgFont(secondProgram()),
                }}>
                  {truncate(secondProgram(), 34)}
                </Text>
              </View>
            </Show>

            {/* Program 3 — only if time exists */}
            <Show when={thirdProgram() && thirdProgramTime()}>
              <View y={84} style={{ flexDirection: "row" }} gap={14}>
                <View width={70} height={34} borderRadius={8} color={hexColor("#12151c")}>
                  <Text x={8} y={8} style={{
                    fontSize: 18,
                    color: hexColor("#9ca3af"),
                    fontFamily: fontFamily(),
                  }}>
                    {thirdProgramTime()}
                  </Text>
                </View>
                <Text x={80} y={6} style={{
                  fontSize: 20,
                  color: hexColor("#d1d5db"),
                  fontFamily: epgFont(thirdProgram()),
                }}>
                  {truncate(thirdProgram(), 34)}
                </Text>
              </View>
            </Show>
          </View>
        </Show>
        {/* ── FAR RIGHT: Clock + date ── */}
        <View x={1780} y={40}>
          <Text style={{
            fontSize: 48,
            color: hexColor("#f0f6fc"),
            fontWeight: isRTL() ? "normal" : "bold",
            fontFamily: fontFamily(),
          }}>
            {currentTime()}
          </Text>
          <Text x={4} y={58} style={{
            fontSize: 20,
            color: hexColor("#9ca3af"),
            fontFamily: fontFamily(),
            fontWeight: isRTL() ? "normal" : "bold",
          }}>
            {currentDate()}
          </Text>
        </View>

      </View>
    </View >
  );
};

export default OSD;